/**
 * Which column of the Partner Portal pipeline a REAL client file belongs in.
 *
 * This is the piece that was missing. `mapProfileToCase` used to hardcode every real client to
 * `status: 'started', profileProgressPct: 50, soaStatus: 'none'`, so a client whose fact-find was
 * finished, whose strategies had been modelled and whose SOA had been generated sat in "In Progress
 * — 50% — No SOA yet", in the same column as a file nobody had touched. The pipeline was therefore
 * decorative: it never moved, whatever anyone did.
 *
 * The stage is DERIVED, not stored, and derived from evidence the server can see without decrypting
 * anything (see ClientProfileWorkflow): how much of the fact-find is filled in, whether any modelling
 * has been saved, and whether an SOA has been generated or published. Nothing here needs a workflow
 * status field that someone has to remember to update — the client's actual progress IS the status,
 * so it can't drift out of sync with reality the way a manually-advanced flag would.
 *
 * Order matters: the checks run from the FURTHEST stage backwards, because progress is cumulative
 * (an SOA implies the fact-find got finished) and the furthest evidence is what the adviser cares about.
 */
import type { ClientProfileWorkflow } from '../../services/profileApi.ts';
import type { DemoCaseStatus } from './demoData.tsx';

export type SoaStatus = 'none' | 'draft' | 'published';

export interface DerivedPipelineStage {
    status: DemoCaseStatus;
    soaStatus: SoaStatus;
    /** 0-100. Falls back to a stage-implied floor when the server hasn't measured the file yet. */
    progressPct: number;
    /** True when progressPct is inferred from the stage rather than measured from the fact-find. */
    progressIsEstimated: boolean;
    /** When the fact-find was first observed complete — what "submitted" means for a real file. */
    submissionDate: string | null;
}

/**
 * Below this, a file is "not really started". An invite flow seeds the client's name from the invite
 * form, which alone can tick the first step — so requiring exactly 0% would move a client out of
 * "Invite Sent" before they had opened the link at all.
 */
const INVITE_UNTOUCHED_MAX_PCT = 15;

/** What each stage implies about progress, for a file the server hasn't measured yet. */
const STAGE_PROGRESS_FLOOR: Record<DemoCaseStatus, number> = {
    draft: 0,
    invited: 0,
    started: 50,
    submitted: 100,
    adviser_review: 100,
    soa_draft: 100,
    soa_published: 100,
};

/**
 * @param workflow  Server-derived progress/SOA/model metadata; undefined on an older server.
 * @param invitedOverride  Forces the invited state. Only used immediately after an adviser sends an
 *                         invite, before the server list has caught up; otherwise it is DERIVED from
 *                         the invite the server reports, so it survives a reload.
 */
export function derivePipelineStage(
    workflow: ClientProfileWorkflow | undefined,
    invitedOverride?: boolean,
): DerivedPipelineStage {
    const measured = typeof workflow?.progressPct === 'number' ? workflow.progressPct : null;
    const soaCount = workflow?.soaCount ?? 0;
    const publishedCount = workflow?.soaPublishedCount ?? 0;
    const modelCount = workflow?.modelCount ?? 0;
    const completedAt = workflow?.completedAt ?? null;
    // An invite that was sent, never accepted, and hasn't been revoked or expired. This is the ONLY
    // thing that distinguishes a client who was sent a link and hasn't opened it from one the adviser
    // created in person — both otherwise look like an untouched file. Derived from the server rather
    // than held in memory, so it survives a page reload; before this, an invited client silently
    // reappeared as "in progress" the moment the portal reloaded.
    const inviteOutstanding =
        invitedOverride ??
        Boolean(workflow?.inviteSentAt && !workflow?.inviteAcceptedAt && !workflow?.inviteClosed);

    const soaStatus: SoaStatus = publishedCount > 0 ? 'published' : soaCount > 0 ? 'draft' : 'none';

    const status: DemoCaseStatus = (() => {
        // An SOA is the furthest a file goes, and a published one outranks a draft.
        if (publishedCount > 0) return 'soa_published';
        if (soaCount > 0) return 'soa_draft';
        // Modelling saved against the file means an adviser has actively worked it.
        if (modelCount > 0) return 'adviser_review';
        // Fact-find finished, nothing modelled yet — this is the queue that needs an adviser.
        if (completedAt || measured === 100) return 'submitted';
        // An outstanding invite on a file with nothing meaningful in it yet: still waiting on the
        // client to open the link. A part-filled file means they HAVE started, invite or not.
        if (inviteOutstanding && (measured === null || measured <= INVITE_UNTOUCHED_MAX_PCT)) return 'invited';
        return 'started';
    })();

    return {
        status,
        soaStatus,
        // A measured percentage always wins. Where the file predates measurement, the stage itself
        // implies a floor — an SOA can't exist against a half-finished fact-find — which is honest
        // about being an inference rather than quietly presenting a made-up number as measured.
        progressPct: measured ?? STAGE_PROGRESS_FLOOR[status],
        progressIsEstimated: measured === null,
        submissionDate: completedAt,
    };
}
